/**
 * Global chunk error handling for Vite dynamic imports.
 *
 * When a new version is deployed, old chunk hashes no longer exist on the server.
 * Users with cached HTML still reference old chunks, causing import failures.
 * This module provides multiple layers of error handling to catch these errors
 * and automatically reload the page to fetch fresh HTML.
 *
 * Error types handled:
 * - Failed to fetch dynamically imported module (Chrome/Chromium)
 * - error loading dynamically imported module (Firefox)
 * - Importing a module script failed (Safari/WebKit)
 * - Unable to preload CSS (Vite preload errors)
 * - Couldn't resolve component (Vue Router - chunk loaded but empty/corrupted)
 *
 * Layers of protection:
 * 1. vite:preloadError - Catches Vite's preload mechanism errors (fires earliest)
 * 2. unhandledrejection - Fallback for any Promise rejections from dynamic imports
 * 3. router.onError - Vue Router's error handler (see router/index.ts)
 *
 * All three layers call `reportChunkError()`, which owns the whole decision:
 * whether the failure is worth acting on at all, then reloading once, then
 * telling the user. None of them may act on their own, because that branch is
 * silent by construction: both listeners call `event.preventDefault()` first (so
 * the browser reports nothing) and `shouldDropSentryEvent` filters chunk errors
 * on purpose (utils/sentryEventFilter). Discarding the decision leaves the user
 * on a page whose lazily-imported component never loaded, with no reload, no
 * message, no console error and no Sentry event (#4181).
 *
 * Uses sessionStorage to prevent infinite reload loops if the error persists.
 */

const CHUNK_ERROR_RELOAD_KEY = 'scrivio_chunk_reload_attempted'

/**
 * What a user is told when reloading did not resolve the chunk error.
 *
 * Bilingual and written out here rather than resolved through i18n: the failure
 * mode is a build whose chunks are gone, so asking for a locale bundle is asking
 * the same build for another file it may not be able to serve. Declared once so
 * the three handlers cannot drift apart on what they say.
 */
const STALE_VERSION_NOTICE =
  'Er is een nieuwe versie beschikbaar. Sluit dit venster en open de pagina opnieuw.\n\n' +
  'A new version is available. Please close this window and reopen the page.'

/**
 * How long a chunk error waits before it is allowed to act.
 *
 * A dynamic import that is still in flight when the user navigates away is
 * *aborted*, and an abort raises the same message a missing chunk does —
 * `Importing a module script failed` on WebKit, `Failed to fetch dynamically
 * imported module` on Chrome, `error loading dynamically imported module` on
 * Firefox. Nothing in the error distinguishes them, so the only thing that does
 * is what the document does next: one that was leaving fires `pagehide`, one
 * that is staying does not.
 *
 * That signal cannot be read synchronously. WebKit stops a document's pending
 * loads when a navigation *starts*, while `pagehide` waits for the replacement
 * document to commit — so at the moment the rejection arrives the flag is still
 * unset, which is how a click on a second link came to be answered with a
 * blocking alert and a hard navigation back to the page the user was leaving
 * (#4409). This window is what bridges the two; the gap in the trace on that
 * ticket was single-digit milliseconds.
 *
 * It costs the genuine case nothing. A chunk the server no longer has is a
 * permanent condition, so a quarter second before reloading changes nothing for
 * the user whose build really is stale — and it is spent behind a page that is
 * already missing the component it was told to render.
 */
const NAVIGATION_SETTLE_MS = 250

/**
 * True once the notice has been shown for the current page.
 *
 * One failed dynamic import reaches more than one handler — Vite's
 * `preloadError` resolves the import with `undefined` once it is prevented,
 * which then surfaces at `router.onError` as "Couldn't resolve component" — so
 * without this the user dismisses the same alert two or three times over.
 * Cleared by `noteNavigationSucceeded()`, i.e. by a navigation that succeeded.
 */
let staleVersionNotified = false

/**
 * True while this document is on its way out.
 *
 * Set by `pagehide` and cleared by `pageshow`, which is what a document
 * restored from the back/forward cache gets instead of a fresh load: without
 * the second half a returning page would carry the flag for the rest of its
 * life and handle no chunk error at all.
 */
let documentIsLeaving = false

/** The single scheduled reaction, if a chunk error is currently waiting one out. */
let pendingReaction: ReturnType<typeof setTimeout> | undefined

/**
 * Tell the user their tab is running against a build that no longer exists.
 *
 * Called when `handleChunkError()` returns false: it has already reloaded once
 * and the chunk still did not load, so there is nothing left to do
 * automatically.
 */
function notifyStaleVersion(): void {
  if (staleVersionNotified) return
  staleVersionNotified = true
  window.alert(STALE_VERSION_NOTICE)
}

/**
 * Check if an error is a chunk loading error from a failed dynamic import.
 *
 * Note that this answers "does this read as a failed dynamic import", not "is
 * this build stale" — an aborted import is indistinguishable by message, which
 * is what `reportChunkError()` exists to sort out.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false

  const message = error.message
  return (
    // Chrome/Chromium - dynamic import failures
    message.includes('Failed to fetch dynamically imported module') ||
    // Firefox - dynamic import failures
    message.includes('error loading dynamically imported module') ||
    // Safari/WebKit - module import failures
    message.includes('Importing a module script failed') ||
    // Generic fetch failures for JS chunks
    (message.includes('Failed to fetch') && message.includes('.js')) ||
    // Vite-specific preload errors
    message.includes('Unable to preload CSS') ||
    // Vue Router component resolution failures (chunk loaded but empty/corrupted)
    message.includes("Couldn't resolve component")
  )
}

/**
 * Handle a chunk loading error by reloading the page (once).
 * Returns true if the error was handled, false otherwise.
 */
function handleChunkError(targetPath?: string): boolean {
  const reloadAttempted = sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY)

  if (!reloadAttempted) {
    // First attempt: set flag and reload
    sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, 'true')
    // Use the target path if provided, otherwise reload current location
    window.location.href = targetPath || window.location.href
    return true
  }

  // Already tried reloading - clear flag for future attempts
  sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY)
  return false
}

/**
 * React to a failed dynamic import — the one entry point all three layers use.
 *
 * `targetPath` is where the user was going, so the reload lands there rather
 * than on the route they were leaving. It is only known at `router.onError`;
 * the two window listeners reload the current location.
 *
 * The reaction is scheduled rather than taken, and abandoned if the document
 * turns out to have been leaving (see `NAVIGATION_SETTLE_MS`). At most one is
 * outstanding: a single failed import reaches several layers, and letting each
 * schedule its own would reload once per layer.
 */
export function reportChunkError(targetPath?: string): void {
  if (documentIsLeaving || pendingReaction !== undefined) return

  pendingReaction = setTimeout(() => {
    pendingReaction = undefined
    if (documentIsLeaving) return
    if (!handleChunkError(targetPath)) notifyStaleVersion()
  }, NAVIGATION_SETTLE_MS)
}

/**
 * Record that a navigation completed. Call this on successful navigation.
 *
 * A navigation that completed is the evidence the app recovered, so the reload
 * flag and the notice are both armed again: the next failure starts from a
 * clean slate. It also drops a reaction that is still waiting out its settle
 * window — the user is on the page they asked for, and reloading them off it
 * over an import that was superseded is the defect, not the cure.
 */
export function noteNavigationSucceeded(): void {
  sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY)
  staleVersionNotified = false
  cancelPendingReaction()
}

function cancelPendingReaction(): void {
  if (pendingReaction === undefined) return
  clearTimeout(pendingReaction)
  pendingReaction = undefined
}

// Vite provides VitePreloadErrorEvent type in client.d.ts
interface VitePreloadErrorEvent extends Event {
  payload: Error
}

/**
 * Initialize global chunk error handlers.
 * Must be called BEFORE any dynamic imports happen (at the top of main.ts).
 */
export function initChunkErrorHandling(): void {
  // Handler for Vite's preload errors (fires when preloaded chunks fail to load)
  // See: https://vitejs.dev/guide/build.html#load-error-handling
  window.addEventListener('vite:preloadError', (event: VitePreloadErrorEvent) => {
    if (isChunkLoadError(event.payload)) {
      // Prevent the error from propagating further
      event.preventDefault()
      reportChunkError()
    }
  })

  // Fallback handler for unhandled Promise rejections from dynamic imports
  // This catches errors that slip through other handlers
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      // Prevent the error from being logged to console
      event.preventDefault()
      reportChunkError()
    }
  })

  // The document is going away: every import it still had in flight is about to
  // be aborted, and none of those aborts says anything about the build.
  window.addEventListener('pagehide', () => {
    documentIsLeaving = true
    cancelPendingReaction()
  })

  // The document is being shown: a fresh load, or — the case that matters — the
  // same document alive again out of the back/forward cache, where `pagehide`
  // has already fired and nothing else would ever unset it.
  window.addEventListener('pageshow', () => {
    documentIsLeaving = false
  })
}
